import type { ChatHistoryMessage } from "@globals";
import type { ChatMessage } from "@lib/agent/types";

// System-event notices are persisted as user-role rows with this prefix (mirrors
// `SYSTEM_NOTICE_PREFIX` in `apps/agent_proxy/service.py`). They are agent-only context
// and must never render in the chat, so they're filtered out at the seed point.
const SYSTEM_MESSAGE_PREFIX = "[SYSTEM] ";

// Strands appends this synthetic user-role message to force a structured-output tool call
// when the agent is built with `structured_output_model`. It is framework-internal and gets
// persisted like any turn, so it must be filtered out here the same way as `[SYSTEM]` rows.
const STRUCTURED_OUTPUT_PROMPT = "You must format the previous response as structured output.";

type ContentBlock = { text?: string };

function flattenContent(content: ChatHistoryMessage["content"]): string {
  if (!Array.isArray(content)) {
    return "";
  }
  return (content as ContentBlock[]).map(block => block?.text ?? "").join("");
}

export function translateBackendMessages(messages: ChatHistoryMessage[]): ChatMessage[] {
  // Strands persists every turn, including `role: "user"` messages whose content is purely
  // `toolResult` blocks and assistant turns that are only `toolUse`. Those have no text and
  // would render as empty chat bubbles, so drop them — tool activity is already conveyed via
  // the streaming `agent.tool_use` path on the live chat side. A message carrying only an
  // attachment (no text) is still meaningful, so it survives the drop on the attachment count.
  return messages.flatMap(m => {
    const content = flattenContent(m.content);
    const attachments = (m.attachments ?? []).map(a => ({ filename: a.filename }));
    if (!content && attachments.length === 0) {
      return [];
    }
    if (
      m.role === "user" &&
      (content.startsWith(SYSTEM_MESSAGE_PREFIX) || content.trim() === STRUCTURED_OUTPUT_PROMPT)
    ) {
      return [];
    }
    return [
      {
        id: `hist-${m.message_index}`,
        role: m.role === "assistant" ? "assistant" : "user",
        content,
        attachments: attachments.length > 0 ? attachments : undefined
      }
    ];
  });
}
